/**
 * Bearer token in localStorage so the session survives app restarts (Capacitor / PWA).
 * Legacy tokens in sessionStorage are migrated on first read. Logout clears both stores.
 * With HTTP-only cookies the backend does not rely on this value.
 */
import type { AuthTokenStoragePort } from '../../domain/ports/AuthTokenStoragePort.ts';

export class AuthStorageAdapter implements AuthTokenStoragePort {
  private readonly storage = globalThis.localStorage;
  private readonly legacyStorage = globalThis.sessionStorage;

  /** Constructs the auth storage adapter. */
  constructor(private readonly sessionTokenKey: string) {}

  /** Retrieves the stored authentication token. */
  getToken(): string | null {
    const persisted = this.storage.getItem(this.sessionTokenKey);
    if (persisted != null) return persisted;
    const legacy = this.legacyStorage.getItem(this.sessionTokenKey);
    if (legacy == null) return null;
    this.storage.setItem(this.sessionTokenKey, legacy);
    this.legacyStorage.removeItem(this.sessionTokenKey);
    return legacy;
  }

  /** Stores the authentication token. */
  setToken(token: string): void {
    this.storage.setItem(this.sessionTokenKey, token);
    this.legacyStorage.removeItem(this.sessionTokenKey);
  }

  /** Clears the stored authentication token. */
  clearToken(): void {
    this.storage.removeItem(this.sessionTokenKey);
    this.legacyStorage.removeItem(this.sessionTokenKey);
  }
}
